// src/com/user_alloc/UserAllocControl.js
import React, { useEffect, useRef, useState } from 'react';
//import { Box } from '@mui/system';
//import { styled } from '@mui/material/styles';
import { Tabs, Tab, DialogContent, DialogActions, Box, Button } from '@mui/material';
import { AppObject } from '../../value/AppConst';
import DraggableEditor from '../../common/DraggableEditor';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useAlertSetter } from '../../api/app-hook';
import {
    authClassSimpleListRepo, comInfoSimpleListRepo, dcInfoSimpleListRepo, storeInfoSimpleListRepo
} from '../../value/AppModel';
import { ADD_USER_ALLOC, EDIT_USER_ALLOC, LIST_LOOKUPS_UNSAFE, REUSE_USER_ALLOC } from './UserAllocGql';
import { useSnackbar } from 'notistack';
import UserAllocInput from './UserAllocInput';
import UserAllocReuse from './UserAllocReuse';


const RequestKey = {
    New: 'New',
    Edit: 'Edit',
    Reuse: 'Reuse'
}
const RequestOptions = [
    {value: RequestKey.New, label: '신규요청'},
    {value: RequestKey.Edit, label: '변경요청'},
    {value: RequestKey.Reuse, label: '사용중지 해제요청'},
];

const RequestTabs = ({
    tabNow,
    onChangeTab,
}) => {
    return(
        <Tabs value={tabNow}
            onChange={(e, v) => onChangeTab(v)}
            variant="fullWidth"
            sx={{borderBottom:1, borderColor:'divider', marginBottom:2}}
        >
            {RequestOptions.map((option) => (
                <Tab key={option.value} label={option.label} value={option.value} />
            ))}
        </Tabs>
    );
};

/**
 * (USER_ALLOC) 정보 생성 또는 편집 Dialog
 * 사용자 승인요청
 * 로그인 화면(AppLogin.js) 아래에 있는 버튼을 누르면 활성화 되는 화면
 * 신규요청, 변경요청, 사용중기 해제요청 3가지 선택에 따라 달라지므로 각각을 서브 컴포넌트로 분리.(~Input, ~Reuse)
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2025-09-19 13:39:47 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function UserAllocControl({
    open,
    onClose,
}) {
    const authClassList = useReactiveVar(authClassSimpleListRepo);
    const dcInfoList = useReactiveVar(dcInfoSimpleListRepo);
    const storeInfoList = useReactiveVar(storeInfoSimpleListRepo);
    const comInfoList = useReactiveVar(comInfoSimpleListRepo);
    const [requestKey, setRequestKey] = useState(RequestKey.New);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const addAlert = useAlertSetter();
    const fetchRef = useRef(false);
    // const dcTypeCdOptions = ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.DC_TYPE_CD, InputMap.dcTypeCd.id);
    // Query for lookup lists.
    const [getLookups, responseLookups] = useLazyQuery(LIST_LOOKUPS_UNSAFE, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetLookups(data, option)},
        onError: (error) => addAlert({title: "기초데이터 조회 오류", message: [error.message]})
    })
    // ##### GraphQL Mutation. ###
    const [addUserAlloc, responseAdd] = useMutation( ADD_USER_ALLOC, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
		onError: (error) => addAlert({title: '신규요청 오류', message: [error.message]})
	} );
    const [editUserAlloc, responseEdit] = useMutation( EDIT_USER_ALLOC, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => addAlert({title: '변경요청 오류', message: [error.message]})
	} );
    const [reuseUserAlloc, responseReuse] = useMutation( REUSE_USER_ALLOC, {
		onCompleted: (data, option) => onCompleteReuse(data, option),
		onError: (error) => addAlert({title: '사용중지 해제 요청 오류', message: [error.message]})
	});

    useEffect(() => {
        if(open && !fetchRef.current) {
            if(authClassList.length===0 || dcInfoList.length===0 || storeInfoList.length===0 || comInfoList.length===0) {
                fetchRef.current = true;
                // fetching...
                getLookups();
            }
        }
    }, [open, authClassList, dcInfoList, storeInfoList, comInfoList, getLookups]);

    const onCompleteGetLookups = (data, clientOption) => {
        let errors = [];
        if(data.authClassListUnsafe) {
            authClassSimpleListRepo(data.authClassListUnsafe);
        }
        else errors.push('권한정보 목록 조회 오류');
        if(data.dcInfoListUnsafe) {
            dcInfoSimpleListRepo(data.dcInfoListUnsafe);
        }
        else errors.push('센터 목록 조회 오류');
        if(data.storeInfoListUnsafe) {
            storeInfoSimpleListRepo(data.storeInfoListUnsafe);
        }
        else errors.push('지점 목록 조회 오류');
        if(data.comInfoListUnsafe) {
            comInfoSimpleListRepo(data.comInfoListUnsafe);
        }
        else errors.push('운송사 목록 조회 오류');

        if(errors.length > 0) addAlert({title:'기초데이터 조회 오류', message:errors});
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.userAllocAdd.ok) {
            enqueueSnackbar('신규요청이 완료되었습니다.', {variant: 'success'});
            onClose();
        }
        else addAlert({title:'신규요청 오류', message:[data.userAllocAdd.message]});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.userAllocEdit.ok) {
            enqueueSnackbar('변경요청이 완료되었습니다.', {variant: 'success'});
            onClose();
        }
        else addAlert({title:'변경요청 오류', message:[data.userAllocEdit.message]});
    };

    const onCompleteReuse = (data, clientOption) => {
        if(data.userAllocReuse.ok) {
            enqueueSnackbar('사용중지 해제 요청이 완료되었습니다.', {variant: 'success'});
            onClose();
        }
        else addAlert({title:'사용중지 해제 요청 오류', message:[data.userAllocReuse.message]});
    };

    const onChangeTab = (tabNow) => {
        setRequestKey(tabNow);
    };

    const RequestComponent = <RequestTabs tabNow={requestKey} onChangeTab={onChangeTab} />;

    // Input field initiallized using getInputField(c,t,i)
    return(
        <DraggableEditor open={open} maxWidth='md'
            title="승인요청"
        >
            {
                authClassList.length===0 || dcInfoList.length===0
                || storeInfoList.length===0 || comInfoList.length===0
                || responseLookups.loading
                ?
                <>
                <DialogContent>
                    <Box style={{padding:60, textAlign:'center', color:'gray', fontSize:'1.2rem'}}>
                        <div>기초데이터를 불러오는 중입니다...</div>
                        <div>잠시만 기다려주세요.</div>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} color="primary" variant="outlined">닫기</Button>
                </DialogActions>
                </>
                :
                requestKey === RequestKey.Reuse
                ?
                <UserAllocReuse
                    RequestTabs={RequestComponent}
                    onClose={onClose}
                    onRequestSave={(param) => reuseUserAlloc({variables:{userAlloc: param}})}
                    responseSaving={responseReuse}
                />
                :
                <UserAllocInput
                    RequestTabs={RequestComponent}
                    onClose={onClose}
                    isEdit={requestKey === RequestKey.Edit}
                    onRequestSave={(param, isEdit) => {
                        if(isEdit) editUserAlloc({variables:{userAlloc: param}});
                        else addUserAlloc({variables:{userAlloc: param}});
                    }}
                    responseSaving={requestKey === RequestKey.Edit ? responseEdit : responseAdd}
                />
            }
        </DraggableEditor>
    );
}
